import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";
import { updateJob, fetchJobs } from "../../../redux/actions/jobActions";
import {
  FiX,
  FiRefreshCw,
  FiEdit,
  FiType,
  FiBriefcase,
  FiMapPin,
  FiClock,
  FiFileText,
  FiCheckCircle,
  FiDollarSign,
  FiAward,
} from "react-icons/fi";

const EditJobs = ({ job, close }: any) => {
  const dispatch = useDispatch<any>();

  // State for all dynamic fields based on your API structure
  const [jobTitle, setJobTitle] = useState("");
  const [department, setDepartment] = useState("");
  const [location, setLocation] = useState("");
  const [jobType, setJobType] = useState("");
  const [experienceRequired, setExperienceRequired] = useState("");
  const [salaryRange, setSalaryRange] = useState("");
  const [description, setDescription] = useState("");
  const [requirements, setRequirements] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (job) {
      setJobTitle(job.job_title || "");
      setDepartment(job.department || "");
      setLocation(job.location || "");
      setJobType(job.job_type || "Full Time");
      setExperienceRequired(job.experience_required || "");
      setSalaryRange(job.salary_range || "");
      setDescription(job.description || "");
      setRequirements(job.requirements || "");
      setStatus(job.status || "active");
    }
  }, [job]);

  const handleUpdate = async (e: any) => {
    e.preventDefault();

    const jobData = {
      job_title: jobTitle,
      department: department,
      location: location,
      job_type: jobType,
      experience_required: experienceRequired,
      salary_range: salaryRange,
      description: description,
      requirements: requirements,
      status: status,
    };

    const success = await dispatch(updateJob(job.id, jobData));
    if (success) {
      dispatch(fetchJobs());
      close();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header - Emerald Theme for Edit Mode */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-6 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-white">
              <div className="p-2 bg-white/20 rounded-lg">
                <FiEdit className="text-xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Edit Job Opening</h2>
                <p className="text-emerald-100 text-sm">
                  Updating details for: {job?.job_title}
                </p>
              </div>
            </div>
            <button
              onClick={close}
              className="text-white/80 hover:text-white transition-colors"
            >
              <FiX size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={handleUpdate} className="p-8 space-y-6">
          {/* Row 1: Job Title & Department */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiType /> Job Title
              </label>
              <input
                type="text"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl py-3 px-4 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiBriefcase /> Department
              </label>
              <input
                type="text"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl py-3 px-4 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500"
                required
              />
            </div>
          </div>

          {/* Row 2: Location, Type & Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiMapPin /> Location
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl py-3 px-4 dark:text-white outline-none"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiClock /> Job Type
              </label>
              <select
                value={jobType}
                onChange={(e) => setJobType(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl py-3 px-4 dark:text-white outline-none"
              >
                <option value="Full Time">Full Time</option>
                <option value="Part Time">Part Time</option>
                <option value="Contract">Contract</option>
                <option value="Internship">Internship</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiCheckCircle /> Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl py-3 px-4 dark:text-white outline-none"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Row 3: Experience & Salary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiAward /> Experience Required
              </label>
              <input
                type="text"
                value={experienceRequired}
                onChange={(e) => setExperienceRequired(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl py-3 px-4 dark:text-white outline-none"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
                <FiDollarSign /> Salary Range
              </label>
              <input
                type="text"
                value={salaryRange}
                onChange={(e) => setSalaryRange(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl py-3 px-4 dark:text-white outline-none"
              />
            </div>
          </div>

          {/* Row 4: Description */}
          <div className="space-y-2">
            <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
              <FiFileText /> Job Description
            </label>
            <textarea
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl py-3 px-4 dark:text-white outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
              required
            />
          </div>

          {/* Row 5: Requirements */}
          <div className="space-y-2">
            <label className="text-sm font-semibold dark:text-gray-300 flex items-center gap-2">
              <FiCheckCircle /> Requirements
            </label>
            <textarea
              rows={5}
              value={requirements}
              onChange={(e) => setRequirements(e.target.value)}
              className="w-full bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-xl py-3 px-4 dark:text-white font-mono text-sm outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
              required
            />
          </div>

          {/* Footer Actions */}
          <div className="flex gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={close}
              className="flex-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-3 rounded-xl font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-200"
            >
              Discard Changes
            </button>

            <motion.button
              type="submit"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{
                scale: 1.02,
                boxShadow: "0 10px 15px -3px rgba(16, 185, 129, 0.3)",
              }}
              whileTap={{ scale: 0.95 }}
              className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 rounded-xl font-bold shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-2 transition-all cursor-pointer"
            >
              <FiRefreshCw className="text-lg" />
              Update Job Post
            </motion.button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditJobs;